import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CreateBedSchema, CreateBedInput } from '@hostel-manager/shared';
import { useFormValidation } from '../../hooks/useFormValidation';
import toast from 'react-hot-toast';

export interface BedFormProps {
  onSubmit: (data: CreateBedInput) => Promise<void>;
  onCancel: () => void;
}

const initialFormState: {
  bedNumber: string;
  room: string;
  rentAmount: number;
  buildingSection: string;
  bedType: 'SINGLE' | 'SHARED' | 'PREMIUM';
} = {
  bedNumber: '',
  room: '',
  rentAmount: 7500,
  buildingSection: 'A',
  bedType: 'SINGLE',
};

/**
 * Add bed form (FR-039, T043).
 * Bed number, room, rent amount.
 */
const BedForm: React.FC<BedFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const { validate } = useFormValidation(CreateBedSchema, {
    showToastOnError: true,
    toastDuration: 5000,
  });

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === 'number' ? value : String(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToValidate = {
      ...formData,
      rentAmount: Number(formData.rentAmount),
    };

    const result = validate(dataToValidate);
    if (!result.valid || !result.data) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(result.data);
      toast.success('Bed added successfully');
      setFormData(initialFormState);
      onCancel();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add bed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Bed Number"
            placeholder="A101 or 101"
            value={formData.bedNumber}
            onChange={handleChange('bedNumber')}
            helperText="Format: A101, B202, or 101"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Room"
            placeholder="Room 101"
            value={formData.room}
            onChange={handleChange('room')}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Monthly Rent (₹)"
            type="number"
            value={formData.rentAmount}
            onChange={handleChange('rentAmount')}
            inputProps={{ min: 1000, max: 50000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Building Section</InputLabel>
            <Select
              value={formData.buildingSection}
              label="Building Section"
              onChange={(e) => setFormData((prev) => ({ ...prev, buildingSection: e.target.value }))}
            >
              {['A', 'B', 'C', 'D', 'E', 'G'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Bed Type</InputLabel>
            <Select
              value={formData.bedType}
              label="Bed Type"
              onChange={(e) => setFormData((prev) => ({ ...prev, bedType: e.target.value as typeof prev.bedType }))}
            >
              <MenuItem value="SINGLE">Single</MenuItem>
              <MenuItem value="SHARED">Shared</MenuItem>
              <MenuItem value="PREMIUM">Premium</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={submitting} sx={{ minHeight: 44 }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} sx={{ minHeight: 44 }}>
            {submitting ? 'Adding...' : 'Add Bed'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default BedForm;
